/* R69: explicit, value-free events; Shopify consent + GPC gates every capture. */
(() => {
  'use strict';
  if (window.MoziPostHog) return;
  const node = document.getElementById('mozi-posthog-config');
  if (!node) return;
  let config;
  try { config = JSON.parse(node.textContent); } catch { return; }
  if (!config.enabled || (config.previewOnly && config.themeRole !== 'unpublished')) return;
  // A separately installed pixel/snippet must be reconciled before enabling this producer.
  if (window.posthog) return;
  const KEY = 'mozi-posthog-entry-v1';
  const EVENTS = new Set(['$pageview', '$pageleave', 'mozi:cta_click', 'mozi:navigation',
    'mozi:scroll_depth', 'mozi:page_engagement', 'mozi:popup_open', 'mozi:dismiss',
    'mozi:form_start', 'mozi:submit_attempt', 'mozi:signup_success', 'mozi:validation_error', 'mozi:save_error',
    'mozi:client_error', 'mozi:web_vital', 'mozi:consent_reaccepted']);
  const LABELS = new Set(['utm_source', 'utm_medium', 'utm_campaign', 'utm_content',
    'utm_term', 'campaign', 'creative', 'market', 'ad_id', 'adset_id']);
  const PROPERTIES = new Set(['token', 'distinct_id', '$session_id', '$window_id', '$device_id',
    '$lib', '$lib_version', '$browser', '$browser_version', '$os', '$os_version', '$device_type',
    '$screen_height', '$screen_width', '$viewport_height', '$viewport_width', '$insert_id',
    '$is_identified', '$process_person_profile', '$geoip_disable', '$current_url', '$pathname', '$host',
    '$referrer', '$referring_domain', '$initial_referrer', '$initial_referring_domain',
    'page', 'landing_page', 'page_observation_id', 'locale', 'shopify_market', 'theme_id', 'release', 'environment',
    'measurement_version', 'consent_segment', 'is_qa', 'consent_state', 'viewport_class', 'cta_id', 'placement',
    'target_page', 'depth_percent', 'active_ms', 'max_depth_percent', 'elapsed_ms',
    'error_category', 'vital_name', 'vital_value', ...LABELS]);
  let sdk = null, loading = false, accepted = false, entry = null;
  let qaStatus = null, qaCount = 0;
  let pageViewed = false, pageLeft = false, maxDepth = 0, activeMs = 0, lastTime = performance.now();
  let startedAt = lastTime, depthHits = new Set(), formStarted = new WeakSet(), lcp = null, cls = 0;
  const qa = config.themeRole !== 'main';
  // Created only after acceptance, retained only for this document's cardinality.
  let pageObservationId = null, consentSegment = 0;
  const label = (value) => typeof value === 'string' && /^[a-zA-Z0-9 _.-]{1,100}$/.test(value) ? value : undefined;
  const MARKET_PREFIXES = ['/zh-cn', '/zh-au', '/zh-tw', '/zh-hk', '/ja-jp',
    '/en-au', '/en-jp', '/en-tw', '/en-hk', '/de-de', '/en-de', '/fr-fr', '/en-fr', '/it-it',
    '/en-it', '/es-es', '/en-es', '/en-eu', '/en-us', '/en-gb', '/zh-hant', '/ja', '/zh'];
  const PAGE_LABELS = Object.freeze({
    '/': '/', '/product': '/pages/product', '/pages/product': '/pages/product',
    '/pages/membership': '/pages/membership',
    '/how-it-works': '/pages/how-it-works', '/pages/how-it-works': '/pages/how-it-works',
    '/shop': '/pages/shop', '/pages/shop': '/pages/shop',
    '/privacy': '/pages/privacy', '/pages/privacy': '/pages/privacy',
    '/pages/health-features': '/pages/health-features', '/pages/experience': '/pages/experience',
    '/pages/why-mozi': '/pages/why-mozi', '/cart': '/cart',
    '/lp-test': '/', '/lp-home': '/', '/lp-product': '/pages/product'
  });
  function pageFor(path) {
    if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//') || /[?#\\]/.test(path)) return '/other';
    const prefix = MARKET_PREFIXES.find(value => path === value || path.startsWith(value + '/'));
    let clean = prefix ? path.slice(prefix.length) || '/' : path;
    if (clean.length > 1) clean = clean.replace(/\/$/, '');
    return PAGE_LABELS[clean] || '/other';
  }
  const page = pageFor(location.pathname);
  function permitted() {
    if (navigator.globalPrivacyControl === true) return false;
    try { if (localStorage.getItem('mozi-statistics-disabled') === '1') return false; } catch { return false; }
    const privacy = window.Shopify?.customerPrivacy;
    // Explicit acceptance is required, even where Shopify permits analytics before a choice.
    return privacy?.currentVisitorConsent?.().analytics === 'yes' && privacy.analyticsProcessingAllowed?.() === true;
  }
  function clearEntry() { entry = null; try { sessionStorage.removeItem(KEY); } catch {} }
  function landing() {
    const now = Date.now();
    try {
      const old = JSON.parse(sessionStorage.getItem(KEY));
      if (old && now - old.updated < 1800000 && now - old.created < 86400000) {
        const restored = {created: old.created, updated: now, landing_page: pageFor(old.landing_page || '/other')};
        for (const name of LABELS) if (label(old[name])) restored[name] = old[name];
        sessionStorage.setItem(KEY, JSON.stringify(restored));
        return restored;
      }
    } catch {}
    const value = {created: now, updated: now, landing_page: page};
    const query = new URLSearchParams(location.search);
    for (const name of LABELS) if (label(query.get(name))) value[name] = query.get(name);
    try { sessionStorage.setItem(KEY, JSON.stringify(value)); } catch {}
    return value;
  }
  function properties(extra = {}) {
    const context = {};
    for (const name of LABELS) if (entry?.[name]) context[name] = entry[name];
    return {...context, page, landing_page: entry?.landing_page || page,
      page_observation_id: pageObservationId,
      locale: label(config.locale) || 'unknown', shopify_market: label(config.market) || 'unknown',
      theme_id: String(config.themeId), release: config.release, environment: qa ? 'preview' : 'production',
      measurement_version: 'posthog_shopify_v3', consent_segment: consentSegment,
      is_qa: qa, consent_state: 'accepted',
      viewport_class: innerWidth <= 760 ? 'narrow' : 'wide',
      $current_url: location.origin + page, $pathname: page, $host: location.host,
      $referrer: '', $referring_domain: '', $initial_referrer: '', $initial_referring_domain: '',
      $process_person_profile: false, $geoip_disable: true, ...extra};
  }
  function beforeSend(event) {
    if (!permitted() || !accepted || !event || !EVENTS.has(event.event)) return null;
    const clean = {};
    for (const [key, value] of Object.entries(event.properties || {})) {
      if (PROPERTIES.has(key) && ['string', 'number', 'boolean'].includes(typeof value)) clean[key] = value;
    }
    event.properties = {...clean, ...properties()};
    return event;
  }
  function capture(name, extra) {
    if (!permitted()) { consentChanged(); return; }
    if (accepted && sdk) {
      sdk.capture(name, properties(extra));
      if (qaStatus) qaStatus.textContent = `Accepted · ${++qaCount} attempted events · latest ${name}`;
    }
  }
  function tick() {
    const now = performance.now();
    if (accepted && permitted() && !document.hidden && document.hasFocus()) activeMs += Math.min(now - lastTime, 2000);
    lastTime = now;
  }
  function depth() {
    if (!accepted || !permitted()) return;
    const height = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
    const visibleBottom = scrollY + innerHeight;
    const percent = visibleBottom >= height - 2 ? 100 : Math.floor(visibleBottom / Math.max(height, 1) * 100);
    maxDepth = Math.max(maxDepth, Math.min(100, percent));
    for (const value of [25, 50, 75, 100]) if (maxDepth >= value && !depthHits.has(value)) {
      depthHits.add(value); capture('mozi:scroll_depth', {depth_percent: value});
    }
  }
  function engagement(leaving) {
    tick();
    if (!accepted || !permitted()) return;
    const extra = {active_ms: Math.round(activeMs), max_depth_percent: maxDepth,
      elapsed_ms: Math.round(performance.now() - startedAt)};
    capture('mozi:page_engagement', extra);
    if (leaving && !pageLeft) { pageLeft = true; capture('$pageleave', extra); }
  }
  function start() {
    if (!sdk || !permitted() || accepted) return;
    if (!pageObservationId) {
      try { pageObservationId = crypto.randomUUID(); } catch { return; }
    }
    consentSegment++;
    accepted = true; entry = landing(); lastTime = performance.now(); startedAt = lastTime;
    sdk.opt_in_capturing({captureEventName: false});
    if (!pageViewed) {
      pageViewed = true;
      capture('$pageview');
      if (document.querySelector('[data-mozi-waitlist-success]')) capture('mozi:signup_success');
    } else {
      capture('mozi:consent_reaccepted');
    }
    depth();
  }
  function consentChanged() {
    if (!permitted()) {
      const wasAccepted = accepted;
      accepted = false; clearEntry(); activeMs = 0;
      // Preserve document pageview/depth cardinality; reaccept starts a new timing segment.
      if (wasAccepted) sdk?.opt_out_capturing({clear_persistence: true});
      if (qaStatus) qaStatus.textContent = 'Analytics off · no new events';
      return;
    }
    if (sdk) return start();
    if (loading) return;
    loading = true;
    const script = document.createElement('script');
    script.src = config.host + '/static/array.js'; script.async = true; script.crossOrigin = 'anonymous';
    script.onload = () => {
      if (!permitted()) { loading = false; return; }
      window.posthog.init(config.token, {api_host: config.host, ui_host: 'https://us.posthog.com',
        defaults: '2026-05-30', persistence: 'sessionStorage', person_profiles: 'identified_only',
        autocapture: false, capture_pageview: false, capture_pageleave: false,
        capture_dead_clicks: false, rageclick: false, capture_heatmaps: false,
        capture_performance: false, capture_exceptions: false, disable_session_recording: true,
        disable_surveys: true, advanced_disable_feature_flags: true, request_batching: false,
        ip: false, opt_out_capturing_by_default: true, before_send: beforeSend,
        loaded: (instance) => { sdk = instance; start(); }});
    };
    script.onerror = () => { loading = false; };
    document.head.appendChild(script);
  }
  document.addEventListener('visitorConsentCollected', consentChanged);
  window.addEventListener('storage', consentChanged);
  document.addEventListener('visibilitychange', () => { tick(); consentChanged(); if (document.hidden) engagement(false); });
  window.addEventListener('pagehide', () => engagement(true));
  window.addEventListener('pageshow', (event) => { if (event.persisted) { pageLeft = false; consentChanged(); } });
  window.addEventListener('scroll', depth, {passive: true});
  // Scalar vitals only; no DOM element, resource URL, stack or network payload.
  if ('PerformanceObserver' in window) {
    try { new PerformanceObserver((list) => { lcp = list.getEntries().at(-1)?.startTime ?? lcp; }).observe({type: 'largest-contentful-paint', buffered: true}); } catch {}
    try { new PerformanceObserver((list) => { for (const item of list.getEntries()) if (!item.hadRecentInput) cls += item.value; }).observe({type: 'layout-shift', buffered: true}); } catch {}
    window.addEventListener('pagehide', () => {
      if (lcp !== null) capture('mozi:web_vital', {vital_name: 'LCP', vital_value: lcp});
      capture('mozi:web_vital', {vital_name: 'CLS', vital_value: cls});
    });
  }
  window.setInterval(() => { tick(); consentChanged(); }, 1000);
  window.setInterval(() => engagement(false), 15000);
  document.addEventListener('click', (event) => {
    const element = event.target.closest?.('[data-home-route-id], [data-purchase-route-id], [data-mozi-preview-open], [data-provisional-cta], a[href]');
    if (!element || element.disabled || element.getAttribute('aria-disabled') === 'true') return;
    const placement = element.closest('[data-mozi-header]') ? 'header' : label(element.closest('[data-analytics-section]')?.dataset.analyticsSection) || 'other';
    const cta = element.hasAttribute('data-home-route-id') || element.hasAttribute('data-purchase-route-id') || element.hasAttribute('data-mozi-preview-open');
    if (cta) capture('mozi:cta_click', {placement, cta_id: label(element.dataset.homeRouteId || element.dataset.purchaseRouteId) || 'waitlist'});
    if (element.matches('a[href]')) {
      const target = new URL(element.getAttribute('href'), location.href);
      if (target.pathname === location.pathname && target.hash) return;
      capture('mozi:navigation', {placement, target_page: target.origin === location.origin ? pageFor(target.pathname) : 'external'});
    }
    if (element.hasAttribute('data-mozi-preview-open')) queueMicrotask(() => {
      if (document.querySelector('[data-mozi-preview-dialog]')?.open) capture('mozi:popup_open', {placement});
    });
  });
  document.addEventListener('close', (event) => { if (event.target.matches?.('[data-mozi-preview-dialog]')) capture('mozi:dismiss'); }, true);
  document.addEventListener('input', (event) => {
    const form = event.target.closest?.('#MoziWaitlistForm');
    if (form && !formStarted.has(form)) { formStarted.add(form); capture('mozi:form_start'); }
  });
  document.addEventListener('submit', (event) => { if (event.target.matches?.('#MoziWaitlistForm')) capture('mozi:submit_attempt'); }, true);
  document.addEventListener('invalid', (event) => { if (event.target.closest?.('#MoziWaitlistForm')) capture('mozi:validation_error'); }, true);
  window.addEventListener('error', (event) => capture('mozi:client_error', {error_category: event instanceof ErrorEvent ? 'script' : 'resource'}), true);
  window.addEventListener('unhandledrejection', () => capture('mozi:client_error', {error_category: 'promise'}));
  window.MoziPostHog = Object.freeze({version: 'posthog_shopify_v3', consentChanged});
  // Explicit preview-only QA controls; absent from normal storefront requests.
  if (qa && new URLSearchParams(location.search).get('mozi_analytics_qa') === '1') {
    const panel = document.createElement('aside');
    panel.style.cssText = 'position:fixed;bottom:50px;left:10px;z-index:2147483000;background:white;color:black;padding:12px;border:1px solid black;font:14px sans-serif';
    const heading = document.createElement('strong'); heading.textContent = 'R69 analytics QA (draft only)'; panel.appendChild(heading);
    qaStatus = document.createElement('p'); qaStatus.textContent = 'Waiting for Shopify privacy API'; panel.appendChild(qaStatus);
    for (const [text, analytics] of [['Accept QA analytics', true], ['Reject QA analytics', false]]) {
      const button = document.createElement('button'); button.type = 'button'; button.textContent = text;
      button.addEventListener('click', () => {
        const privacy = window.Shopify?.customerPrivacy;
        if (!privacy?.setTrackingConsent) { qaStatus.textContent = 'Shopify privacy API unavailable'; return; }
        privacy.setTrackingConsent({analytics}, () => { consentChanged(); });
      });
      panel.appendChild(button);
    }
    document.body.appendChild(panel);
  }
  if (window.Shopify?.customerPrivacy) consentChanged();
  else window.Shopify?.loadFeatures?.([{name: 'consent-tracking-api', version: '0.2'}], (error) => { if (!error) consentChanged(); });
})();
